import { computed, onBeforeMount, ref, watch, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import DateTimeUtils from '../../helpers/DateTimeUtils';

export const MS_PER_MINUTE = 60_000;

export const INTERVAL_OPTIONS = [
  { label: '15s', seconds: 15 },
  { label: '30s', seconds: 30 },
  { label: '1m', seconds: 60 },
  { label: '2m', seconds: 120 },
  { label: '5m', seconds: 300 },
] as const;

export interface Preset {
  label: string;
  minutes: number;
}

export interface LiveModePayload {
  enabled: boolean;
  minutes: number | null;
  label: string;
  refreshIntervalSeconds: number;
}

export interface UseTimeRangePickerOptions {
  defaultTime: Ref<number>;
  onEmitDate: (value: [string, string]) => void;
  onEmitLiveMode: (value: LiveModePayload) => void;
  onClose: () => void;
}

// DOM concerns (panel open/close/position) remain in the component.
export function useTimeRangePicker({
  defaultTime,
  onEmitDate,
  onEmitLiveMode,
  onClose,
}: UseTimeRangePickerOptions) {
  const { t } = useI18n();

  const _now = new Date();
  _now.setSeconds(0);
  _now.setMilliseconds(0);
  const currentDate = ref<[string, string]>([
    new Date(_now.getTime() - defaultTime.value * MS_PER_MINUTE).toISOString(),
    _now.toISOString(),
  ]);

  const fromDateIso = ref<string | null>(null);
  const toDateIso = ref<string | null>(null);

  const presets = ref<Preset[]>([]);
  const selectedPreset = ref<Preset | null>(null);
  const liveEnabled = ref(false);
  const refreshIntervalSeconds = ref(60);

  const formattedDateRange = computed(() => {
    const [start, end] = currentDate.value;
    if (!start || !end) return '';
    return `${isoToDisplayString(start)} - ${isoToDisplayString(end)}`;
  });

  const activeIntervalLabel = computed(
    () => INTERVAL_OPTIONS.find((o) => o.seconds === refreshIntervalSeconds.value)?.label ?? '1m'
  );

  const applyButtonLabel = computed(() =>
    selectedPreset.value && liveEnabled.value
      ? t('timePicker.applyLive')
      : t('timePicker.applyAbsolute')
  );

  function isoToDisplayString(iso: string): string {
    if (!iso) return '';
    try {
      const d = DateTimeUtils.adjustDateToUTC(new Date(iso));
      return DateTimeUtils.formatDateWithConditionalSeconds(d);
    } catch {
      return '';
    }
  }

  function syncAbsoluteFieldsFromCurrentDate(): void {
    fromDateIso.value = currentDate.value[0];
    toDateIso.value = currentDate.value[1];
  }

  function loadPresets(): void {
    presets.value = [
      { label: t('datePicker.presets.last5Minutes'), minutes: 5 },
      { label: t('datePicker.presets.last15Minutes'), minutes: 15 },
      { label: t('datePicker.presets.last30Minutes'), minutes: 30 },
      { label: t('datePicker.presets.last1Hour'), minutes: 60 },
      { label: t('datePicker.presets.last2Hours'), minutes: 120 },
      { label: t('datePicker.presets.last3Hours'), minutes: 180 },
      { label: t('datePicker.presets.last4Hours'), minutes: 240 },
      { label: t('datePicker.presets.last12Hours'), minutes: 720 },
      { label: t('datePicker.presets.last24Hours'), minutes: 1440 },
    ];
  }

  function detectCurrentPreset(): void {
    if (!currentDate.value[0] || !currentDate.value[1]) return;
    if (selectedPreset.value) return;

    // Only auto-select when the end time is close to "now" so the live toggle makes sense

    const end = new Date(currentDate.value[1]);
    const start = new Date(currentDate.value[0]);
    const diffMinutes = Math.round((end.getTime() - start.getTime()) / MS_PER_MINUTE);
    const endAgeMs = Math.abs(Date.now() - end.getTime());

    if (endAgeMs <= 5 * MS_PER_MINUTE) {
      const matched = presets.value.find((p) => p.minutes === diffMinutes);
      if (matched) selectedPreset.value = matched;
    }
  }

  function handlePresetClick(preset: Preset): void {
    selectedPreset.value = preset;
    const now = new Date();
    const start = new Date(now.getTime() - preset.minutes * MS_PER_MINUTE);
    fromDateIso.value = start.toISOString();
    toDateIso.value = now.toISOString();
  }

  function apply(): void {
    if (selectedPreset.value && liveEnabled.value) {
      // Live mode: always recalculate the rolling window from the current time.
      // The flush:'sync' watcher on currentDate emits update:date synchronously
      // before onEmitLiveMode is called, preserving the required event order.
      const now = new Date();
      const start = new Date(now.getTime() - selectedPreset.value.minutes * MS_PER_MINUTE);
      currentDate.value = [start.toISOString(), now.toISOString()];
      onEmitLiveMode({
        enabled: true,
        minutes: selectedPreset.value.minutes,
        label: selectedPreset.value.label,
        refreshIntervalSeconds: refreshIntervalSeconds.value,
      });
    } else {
      // Absolute mode: respect the explicitly selected time range from the date fields.
      const start = fromDateIso.value;
      const end = toDateIso.value;
      if (!start || !end) return;
      currentDate.value = [start, end];
      onEmitLiveMode({
        enabled: false,
        minutes: null,
        label: '',
        refreshIntervalSeconds: refreshIntervalSeconds.value,
      });
    }
    onClose();
  }

  function changeTime(newDate: [string, string]): void {
    currentDate.value = newDate;
  }

  function disableLive(): void {
    liveEnabled.value = false;
  }

  function resetTimeToDefault(): void {
    const now = new Date();
    now.setSeconds(0);
    now.setMilliseconds(0);
    currentDate.value = [
      new Date(now.getTime() - defaultTime.value * MS_PER_MINUTE).toISOString(),
      now.toISOString(),
    ];
  }

  // flush:'sync' ensures the update:date emission happens synchronously when
  // currentDate changes (e.g. inside apply()), so it always precedes the
  // subsequent onEmitLiveMode call and no duplicate explicit calls are needed.
  watch(
    currentDate,
    (val) => {
      if (val) onEmitDate(val);
    },
    { immediate: true, flush: 'sync' }
  );
  watch(selectedPreset, (val) => {
    if (!val) liveEnabled.value = false;
  });

  onBeforeMount(loadPresets);

  return {
    currentDate,
    fromDateIso,
    toDateIso,
    presets,
    selectedPreset,
    liveEnabled,
    refreshIntervalSeconds,
    formattedDateRange,
    activeIntervalLabel,
    applyButtonLabel,
    isoToDisplayString,
    syncAbsoluteFieldsFromCurrentDate,
    loadPresets,
    detectCurrentPreset,
    handlePresetClick,
    apply,
    changeTime,
    disableLive,
    resetTimeToDefault,
  };
}
